<!--~
  ~ Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
  ~ to an End-User License Agreement included in a separate file named EULA.txt
  ~ that is distributed with this file.
  -->
<div *ngIf="definition" fxLayout="column" fxLayoutAlign="start stretch" fxFlex>

    <dash-gis-report-content-filter *ngIf='shouldDisplayFilterUi'
                              [definition]="definition"
                              [id]="id"
                              fxFlex="0 0 auto"></dash-gis-report-content-filter>
    <div class="report-table-container">
        <div class="overlay loading-container" [class.loading]="dataSource.loading$ | async">
            <mat-progress-spinner mode="indeterminate" diameter="60" strokeWidth="15" color="primary"></mat-progress-spinner>
            <div class="report-loading-overlay"></div>
        </div>
        <ng-container *ngIf="dataSource?.hasHtml$ | async; then htmlTemplate; else dataTemplate"></ng-container>
        <ng-container *ngIf="!tableEditActive; else tableEditActions">
            <mat-paginator [hidePageSize]="(dataSource?.count$ | async) <= (pagingOptions$ | async)?.limit"
                           [pageIndex]="(pagingOptions$ | async)?.page"
                           [pageSize]="(pagingOptions$ | async)?.limit"
                           [pageSizeOptions]="pageSizeOptions.values"
                           [length]="dataSource?.count$ | async"
                           (page)="pageChange($event)" fxFlex="0 0 auto"
                           class="no-print">
            </mat-paginator>
        </ng-container>
    </div>
</div>

<ng-template #dataTemplate>
    <form class="selectable-text tabular-data-form" fxLayout="column" fxFlex [formGroup]="dataFormGroup">
        <div class="table-container" fxFlex fxFlexAlign="stretch">
            <table mat-table [dataSource]="dataSource" matSort (matSortChange)="sortChange($event)">
                <ng-container *ngIf="mapEnabled && definition.enableZoom" [matColumnDef]="'zoom'">
                    <th mat-header-cell *matHeaderCellDef>Zoom</th>
                    <td mat-cell *matCellDef="let row">
                        <dash-gis-zoom-button [zoomLayer]="definition.layerField ? row[definition.layerField] : definition.layer"
                                        [zoomObjectKey]="row[definition.dbKey]"
                                        [zoomObjectField]="definition.objectKey"></dash-gis-zoom-button>
                    </td>
                </ng-container>
                <ng-container *ngIf="definition.totalFields">
                    <td mat-footer-cell *matFooterCellDef>Totals:</td>
                </ng-container>
                <ng-container *ngIf="definition.averageFields">
                    <td mat-footer-cell *matFooterCellDef>Averages:</td>
                </ng-container>

                <ng-container *ngFor="let field of definition.fields; let i = index" [matColumnDef]="field.name">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ field.display }}</th>
                    <td mat-cell *matCellDef="let row"
                        [style.max-width]="field.maxWidth || ''"
                        [style.min-width]="field.minWidth || ''"
                        [class.no-wrap]="!field.wrap">
                        <ng-container *ngIf="tableEditActive">
                            <dash-gis-base-field [fieldDefinition]="field" [rowData]="row"
                                           [tableEditActive]="true"
                                           [dataFormGroup]="dataFormGroup"
                                           [dataForm]="dataForm"></dash-gis-base-field>
                        </ng-container>
                        <ng-container *ngIf="!tableEditActive">
                            <dash-gis-base-field [fieldDefinition]="field" [rowData]="row"
                                           [tableEditActive]="false"></dash-gis-base-field>
                        </ng-container>
                    </td>
                </ng-container>

                <ng-container *ngIf="definition.rowDataFormName" [matColumnDef]="'edit'">
                    <th mat-header-cell *matHeaderCellDef>Edit</th>
                    <td mat-cell *matCellDef="let row">
                        <button mat-icon-button
                                (click)="editRowData.emit({
                                        name: definition.rowDataFormName,
                                        keys: [ row[definition.rowDataFormKeyField || definition.dbKey] ]
                                    })">
                            <mat-icon svgIcon="edit"></mat-icon>
                        </button>
                    </td>
                </ng-container>

                <!-- totals -->
                <ng-container *ngFor="let fieldName of columnsToDisplay; let i = index"
                              [matColumnDef]="'total_' + fieldName">
                    <ng-container *ngIf="definition.totalFields">
                        <ng-container [ngSwitch]="getTotalsCellDisplay(fieldName, i)">
                            <ng-container *ngSwitchCase="'label'">
                                <td mat-footer-cell *matFooterCellDef>Totals:</td>
                            </ng-container>
                            <ng-container *ngSwitchCase="'value'">
                                <td mat-footer-cell *matFooterCellDef>
                                    {{ getTotalsValue$(fieldName) | async | number: '1.2' }}
                                </td>
                            </ng-container>
                            <ng-container *ngSwitchDefault>
                                <td mat-footer-cell *matFooterCellDef></td>
                            </ng-container>
                        </ng-container>
                        <ng-container *ngIf="!definition.enableZoom && i === 0">
                            <td mat-footer-cell *matFooterCellDef>Totals:</td>
                        </ng-container>
                    </ng-container>
                </ng-container>

                <!-- averages -->
                <ng-container *ngFor="let fieldName of columnsToDisplay; let i = index"
                              [matColumnDef]="'average_' + fieldName">
                    <ng-container *ngIf="definition.averageFields">
                        <ng-container [ngSwitch]="getAveragesCellDisplay(fieldName, i)">
                            <ng-container *ngSwitchCase="'label'">
                                <td mat-footer-cell *matFooterCellDef>Averages:</td>
                            </ng-container>
                            <ng-container *ngSwitchCase="'value'">
                                <td mat-footer-cell *matFooterCellDef>
                                    {{ getAveragesValue$(fieldName) | async | number: '1.2' }}
                                </td>
                            </ng-container>
                            <ng-container *ngSwitchDefault>
                                <td mat-footer-cell *matFooterCellDef></td>
                            </ng-container>
                        </ng-container>
                        <ng-container *ngIf="!definition.enableZoom && i === 0">
                            <td mat-footer-cell *matFooterCellDef>Averages:</td>
                        </ng-container>
                    </ng-container>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="columnsToDisplay; sticky: true"></tr>
                <tr mat-row *matRowDef="let rowData; columns: columnsToDisplay"></tr>
                <ng-container *ngIf="definition.totalFields">
                    <tr mat-footer-row *matFooterRowDef="totalsColumnsToDisplay"></tr>
                </ng-container>
                <ng-container *ngIf="definition.averageFields">
                    <tr mat-footer-row *matFooterRowDef="averagesColumnsToDisplay"></tr>
                </ng-container>
            </table>
        </div>
    </form>
</ng-template>

<ng-template #htmlTemplate>
    <div class="selectable-text"
         fxLayout="column"
         fxLayoutAlign="space-between start"
         fxFlexAlign="stretch"
         fxFlex="1 1 auto"
         [innerHTML]="dataSource?.reportHtml"></div>
</ng-template>

<ng-template #tableEditActions>
    <div class="edit-actions-container">
        <div class="table-edit-actions" fxLayout="row" fxLayoutAlign="end center">
            <button mat-button (click)="tableEditSave()">Save</button>
            <button mat-button (click)="tableEditCancel()">Cancel</button>
        </div>
    </div>
</ng-template>
